import { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Sparkles, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/LanguageSelector";
import { detectAndApplyLanguage } from "@/lib/languageDetection";
import { trackStoreClick } from "@/lib/onboardingAnalytics";
import MiniGridLoader from '@/components/MiniGridLoader';

// Notification data for iPhone display
interface NotificationData {
  time: string;
  text: string;
  icon?: string;
}

// iPhone frame component that displays video content
interface IPhoneFrameProps {
  videoSrc?: string;
  placeholder?: boolean;
  placeholderText?: string;
  notifications?: NotificationData[];
  wiggly?: boolean;
  customContent?: React.ReactNode;
  thumbnails?: string[];
}

const IPhoneFrame = ({ videoSrc, placeholder = false, placeholderText, notifications, wiggly, customContent, thumbnails }: IPhoneFrameProps) => {
  const isMobile = useIsMobile();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(!!videoSrc && !placeholder);
  const [videoProgress, setVideoProgress] = useState(0);
  const [slideshowIndex, setSlideshowIndex] = useState(0);

  // Slideshow cycling while video is loading
  useEffect(() => {
    if (!thumbnails || thumbnails.length === 0 || !isVideoLoading) return;
    const interval = setInterval(() => {
      setSlideshowIndex((prev) => (prev + 1) % thumbnails.length);
    }, 1200);
    return () => clearInterval(interval);
  }, [thumbnails, isVideoLoading]);

  // Only play video when it's visible on screen
  useEffect(() => {
    if (!videoSrc || placeholder) return;
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [videoSrc, placeholder]);

  return (
    <div ref={containerRef} className={`relative ${isMobile ? 'w-[220px]' : 'w-[280px] md:w-[300px] lg:w-[320px]'} aspect-[9/19.5] flex-shrink-0`}>
      {/* iPhone outer frame — minimal glass */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/[0.06] to-white/[0.02] rounded-[2.5rem] md:rounded-[3rem] shadow-2xl shadow-black/40 border border-white/[0.08]">
        {/* Dynamic Island */}
        <div className="absolute top-2 md:top-3 left-1/2 -translate-x-1/2 w-[70px] md:w-[90px] h-[22px] md:h-[28px] bg-black rounded-full z-20" />
        
        {/* Screen area */}
        <div className="absolute inset-[8px] md:inset-[10px] bg-black/90 rounded-[2rem] md:rounded-[2.5rem] overflow-hidden">
          {/* Video/content area */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 to-black">
            {customContent ? (
              customContent
            ) : notifications && notifications.length > 0 ? (
              <NotificationDisplay notifications={notifications} wiggly={wiggly} />
            ) : placeholder || !videoSrc ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center text-muted-foreground px-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-primary animate-pulse" />
                  </div>
                  <p className="text-xs md:text-sm opacity-60">{placeholderText}</p>
                </div>
              </div>
            ) : (
              <>
                {isVideoLoading && thumbnails && thumbnails.length > 0 ? (
                  <div className="absolute inset-0 z-10">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={slideshowIndex}
                        src={thumbnails[slideshowIndex]}
                        alt=""
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="w-full h-full object-cover"
                      />
                    </AnimatePresence>
                    {/* Dots indicator */}
                    <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-20">
                      {thumbnails.map((_, i) => (
                        <div
                          key={i}
                          className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                            i === slideshowIndex ? 'bg-white scale-110' : 'bg-white/30'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                ) : isVideoLoading ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                ) : null}
                <video
                  ref={videoRef}
                  src={videoSrc}
                  loop
                  muted
                  playsInline
                  preload="none"
                  onCanPlay={() => setIsVideoLoading(false)}
                  onTimeUpdate={(e) => {
                    const v = e.currentTarget;
                    if (v.duration) setVideoProgress((v.currentTime / v.duration) * 100);
                  }}
                  className="w-full h-full object-cover"
                />
                {/* Tiny looping progress bar */}
                {!isVideoLoading && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10">
                    <div
                      className="h-full bg-white/60 transition-[width] duration-200 ease-linear"
                      style={{ width: `${videoProgress}%` }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Side buttons — subtle on glass frame */}
        <div className="hidden sm:block">
          <div className="absolute -left-[2px] md:-left-[3px] top-[100px] md:top-[120px] w-[2px] md:w-[3px] h-6 md:h-8 bg-white/10 rounded-l" />
          <div className="absolute -left-[2px] md:-left-[3px] top-[140px] md:top-[170px] w-[2px] md:w-[3px] h-10 md:h-14 bg-white/10 rounded-l" />
          <div className="absolute -left-[2px] md:-left-[3px] top-[190px] md:top-[230px] w-[2px] md:w-[3px] h-10 md:h-14 bg-white/10 rounded-l" />
          <div className="absolute -right-[2px] md:-right-[3px] top-[130px] md:top-[160px] w-[2px] md:w-[3px] h-16 md:h-20 bg-white/10 rounded-r" />
        </div>
      </div>
    </div>
  );
};

// iOS-style notification display inside iPhone frame
const NotificationDisplay = ({ notifications, wiggly }: { notifications: NotificationData[]; wiggly?: boolean; }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.05 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Cycle through notifications one at a time when wiggly
  useEffect(() => {
    if (!wiggly || !isVisible) return;
    setActiveIndex(0);
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % notifications.length);
    }, 2400);
    return () => clearInterval(interval);
  }, [wiggly, isVisible, notifications.length]);

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col justify-center px-3 md:px-4 gap-3 md:gap-4 py-10 md:py-14">
      {/* iOS lock screen time */}
      <div className="text-center mb-1 md:mb-2">
        <div className="text-white/90 text-xl md:text-3xl font-light">9:41</div>
        <div className="text-white/50 text-[9px] md:text-xs">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
      </div>

      {wiggly ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, y: 40, scale: 0.8, rotate: -2 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: [1, 1.04, 0.97, 1.02, 1],
              rotate: [0, -1.5, 1.5, -1, 0.5, 0],
            }}
            exit={{ opacity: 0, y: -30, scale: 0.85 }}
            transition={{
              opacity: { duration: 0.25 },
              y: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
              scale: { duration: 1.6, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" },
              rotate: { duration: 1.2, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" },
            }}
            className="bg-white/[0.18] backdrop-blur-xl rounded-2xl p-2.5 md:p-3 flex items-start gap-2 md:gap-2.5 border border-white/[0.15] shadow-lg shadow-white/5"
          >
            {/* App icon */}
            <div className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-xl overflow-hidden bg-black/30 flex items-center justify-center">
              <img
                src="/brand-logo-nobg.png"
                alt="GitFit.AI"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Notification content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <span className="text-white/90 text-[10px] md:text-xs font-semibold truncate">GitFit.AI</span>
                <span className="text-white/40 text-[9px] md:text-[11px] flex-shrink-0">{notifications[activeIndex].time}</span>
              </div>
              <p className="text-white/80 text-[9px] md:text-[11px] leading-snug mt-0.5">
                {t(notifications[activeIndex].text as any)}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      ) : notifications.map((notif, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={isVisible ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 30, scale: 0.9 }}
          transition={{ duration: 0.4, delay: i * 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="bg-white/[0.15] backdrop-blur-xl rounded-2xl p-2.5 md:p-3 flex items-start gap-2 md:gap-2.5 border border-white/[0.1]"
        >
          {/* App icon */}
          <div className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-xl overflow-hidden bg-black/30 flex items-center justify-center">
            {notif.icon === 'apple' ? (
              <svg className="w-5 h-5 md:w-6 md:h-6 text-white" viewBox="0 0 384 512" fill="currentColor">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
            ) : notif.icon === 'github' ? (
              <svg className="w-5 h-5 md:w-6 md:h-6 text-white" viewBox="0 0 496 512" fill="currentColor">
                <path d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8z"/>
              </svg>
            ) : notif.icon === 'discord' ? (
              <svg className="w-5 h-5 md:w-6 md:h-6 text-[#5865F2]" viewBox="0 0 640 512" fill="currentColor">
                <path d="M524.531,69.836a1.5,1.5,0,0,0-.764-.7A485.065,485.065,0,0,0,404.081,32.03a1.816,1.816,0,0,0-1.923.91,337.461,337.461,0,0,0-14.9,30.6,447.848,447.848,0,0,0-134.426,0,309.541,309.541,0,0,0-15.135-30.6,1.89,1.89,0,0,0-1.924-.91A483.689,483.689,0,0,0,116.085,69.137a1.712,1.712,0,0,0-.788.676C39.068,183.651,18.186,294.69,28.43,404.354a2.016,2.016,0,0,0,.765,1.375A487.666,487.666,0,0,0,176.02,479.918a1.9,1.9,0,0,0,2.063-.676A348.2,348.2,0,0,0,208.12,430.4a1.86,1.86,0,0,0-1.019-2.588,321.173,321.173,0,0,1-45.868-21.853,1.885,1.885,0,0,1-.185-3.126c3.082-2.309,6.166-4.711,9.109-7.137a1.819,1.819,0,0,1,1.9-.256c96.229,43.917,200.41,43.917,295.5,0a1.812,1.812,0,0,1,1.924.233c2.944,2.426,6.027,4.851,9.132,7.16a1.884,1.884,0,0,1-.162,3.126,301.407,301.407,0,0,1-45.89,21.83,1.875,1.875,0,0,0-1,2.611,391.055,391.055,0,0,0,30.014,48.815,1.864,1.864,0,0,0,2.063.7A486.048,486.048,0,0,0,610.7,405.729a1.882,1.882,0,0,0,.765-1.352C623.729,277.594,590.933,167.465,524.531,69.836ZM222.491,337.58c-28.972,0-52.844-26.587-52.844-59.239S193.056,219.1,222.491,219.1c29.665,0,53.306,26.82,52.843,59.239C275.334,310.993,251.924,337.58,222.491,337.58Zm195.38,0c-28.971,0-52.843-26.587-52.843-59.239S388.437,219.1,417.871,219.1c29.667,0,53.307,26.82,52.844,59.239C470.715,310.993,447.538,337.58,417.871,337.58Z"/>
              </svg>
            ) : (
              <img
                src="/brand-logo-nobg.png"
                alt="GitFit.AI"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          {/* Notification content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1">
              <span className="text-white/90 text-[10px] md:text-xs font-semibold truncate">{notif.icon === 'apple' ? 'Apple Health' : notif.icon === 'github' ? 'GitHub' : notif.icon === 'discord' ? 'Discord' : 'GitFit.AI'}</span>
              <span className="text-white/40 text-[9px] md:text-[11px] flex-shrink-0">{notif.time}</span>
            </div>
            <p className="text-white/80 text-[9px] md:text-[11px] leading-snug mt-0.5">
              {t(notif.text as any)}
            </p>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Mock UI for research-grade accuracy feature
const ResearchAccuracyDisplay = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState(0); // 0=hidden, 1=searching, 2=found, 3=sources
  const [animKey, setAnimKey] = useState(0); // force remount on each re-entry

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let timeouts: ReturnType<typeof setTimeout>[] = [];
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Bump key to force remount so framer-motion re-applies initial styles
          setAnimKey(k => k + 1);
          setPhase(0);
          timeouts.push(setTimeout(() => setPhase(1), 100));   // searching...
          timeouts.push(setTimeout(() => setPhase(2), 1000));  // found sources
          timeouts.push(setTimeout(() => setPhase(3), 1400));  // show sources
        } else {
          setPhase(0);
          timeouts.forEach(clearTimeout);
          timeouts = [];
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(container);
    return () => {
      observer.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, []);

  const sources = [
    { name: "USDA FoodData Central", url: "fdc.nal.usda.gov" },
    { name: "Nutritionix Database", url: "nutritionix.com" },
    { name: "Restaurant Menu Data", url: "menuwithprice.com" },
  ];

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col justify-center px-3 md:px-4 gap-3 md:gap-4">
      <div key={animKey} className="contents">
      {/* Chat bubble - user message */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={phase >= 1 ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        transition={{ duration: 0.3 }}
        className="self-end bg-blue-600 rounded-2xl rounded-br-sm px-3 py-1.5 md:px-3.5 md:py-2 max-w-[85%]"
      >
        <p className="text-white text-[8px] md:text-[10px]">{t('showcase.accuracy_display.user_message')}</p>
      </motion.div>

      {/* AI response area */}
      <div className="self-start max-w-[90%]">
        {/* Searching indicator */}
        <AnimatePresence mode="wait">
          {phase === 1 && (
            <motion.div
              key="searching"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-1.5 mb-2"
            >
              <MiniGridLoader className="w-3 h-3 md:w-3.5 md:h-3.5 text-white/80" />
              <span className="text-white/60 text-[7px] md:text-[9px]">{t('showcase.accuracy_display.searching')}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Found sources */}
        <AnimatePresence>
          {phase >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-1.5 mb-2 md:mb-3"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 15 }}
              >
                <svg className="w-3 h-3 md:w-4 md:h-4 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </motion.div>
              <span className="text-white/80 text-[7px] md:text-[9px] font-medium">{t('showcase.accuracy_display.found_sources')}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Source list */}
        <AnimatePresence>
          {phase >= 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="bg-white/[0.08] backdrop-blur-lg rounded-xl p-2 md:p-2.5 border border-white/[0.06] space-y-1.5 md:space-y-2"
            >
              {sources.map((source, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.15, duration: 0.25 }}
                  className="flex items-center gap-1.5 md:gap-2"
                >
                  <div className="w-4 h-4 md:w-5 md:h-5 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[6px] md:text-[8px]">{"🔗"}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-white/80 text-[7px] md:text-[9px] font-medium truncate">{source.name}</div>
                    <div className="text-white/40 text-[6px] md:text-[7px] truncate">{source.url}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Calorie result */}
        <AnimatePresence>
          {phase >= 3 && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.3 }}
              className="mt-2 md:mt-3 bg-white/[0.06] rounded-xl p-2 md:p-2.5 border border-white/[0.06]"
            >
              <p className="text-white/80 text-[7px] md:text-[9px] leading-relaxed">
                {t('showcase.accuracy_display.user_message')}
              </p>
              <p className="text-white/90 text-[9px] md:text-[11px] font-semibold mt-1">
                {t('showcase.accuracy_display.calories')}
              </p>
              <p className="text-white/40 text-[6px] md:text-[7px] mt-0.5">
                {t('showcase.accuracy_display.error_note')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </div>
  );
};

// Thumbnail mappings for video loading slideshows
const VIDEO_THUMBNAILS: Record<string, string[]> = {
  "/MainVid.mp4": [
    "/showcase_thumbnails/main_1.webp",
    "/showcase_thumbnails/main_2.webp",
    "/showcase_thumbnails/main_3.webp",
  ],
  "/CustomizationFast.mp4": [
    "/showcase_thumbnails/customization_1.webp",
    "/showcase_thumbnails/customization_2.webp",
    "/showcase_thumbnails/customization_3.webp",
  ],
  "/AskAIFast.mp4": [
    "/showcase_thumbnails/askai_1.webp",
    "/showcase_thumbnails/askai_2.webp",
    "/showcase_thumbnails/askai_3.webp",
  ],
  "/progresspics.mp4": [
    "/showcase_thumbnails/progress_1.webp",
    "/showcase_thumbnails/progress_2.webp",
    "/showcase_thumbnails/progress_3.webp",
  ],
  "/Leaderboards.mp4": [
    "/showcase_thumbnails/leaderboards_1.webp",
    "/showcase_thumbnails/leaderboards_2.webp",
    "/showcase_thumbnails/leaderboards_3.webp",
  ],
};

// Feature section data
interface Feature {
  id: string;
  emoji: string;
  title: string;
  subtitle: string;
  description?: string[];
  videoSrc?: string;
  notifications?: NotificationData[];
  wiggly?: boolean;
  customContent?: 'accuracy';
}

// Feature definitions using translation keys (resolved at render time via t())
const features: Feature[] = [
  {
    id: "track",
    emoji: "🌈",
    title: "showcase.features.track.title",
    subtitle: "showcase.features.track.subtitle",
    videoSrc: "/MainVid.mp4",
  },
  {
    id: "customize",
    emoji: "🎨",
    title: "showcase.features.customize.title",
    subtitle: "showcase.features.customize.subtitle",
    videoSrc: "/CustomizationFast.mp4",
  },
  {
    id: "accuracy",
    emoji: "🎯",
    title: "showcase.features.accuracy.title",
    subtitle: "showcase.features.accuracy.subtitle",
    customContent: 'accuracy',
  },
  {
    id: "insights",
    emoji: "🧠",
    title: "showcase.features.insights.title",
    subtitle: "showcase.features.insights.subtitle",
    videoSrc: "/AskAIFast.mp4",
  },
  {
    id: "progress",
    emoji: "📸",
    title: "showcase.features.progress.title",
    subtitle: "showcase.features.progress.subtitle",
    videoSrc: "/progresspics.mp4",
  },
  {
    id: "social",
    emoji: "🏆",
    title: "showcase.features.social.title",
    subtitle: "showcase.features.social.subtitle",
    videoSrc: "/Leaderboards.mp4",
  },
  {
    id: "sync",
    emoji: "🔄",
    title: "showcase.features.sync.title",
    subtitle: "showcase.features.sync.subtitle",
    notifications: [
      { time: "now", text: "showcase.notifications.sync_apple", icon: "apple" },
      { time: "2m ago", text: "showcase.notifications.sync_github", icon: "github" },
      { time: "15m ago", text: "showcase.notifications.sync_discord", icon: "discord" },
    ],
  },
  {
    id: "reminders",
    emoji: "⏰",
    title: "showcase.features.reminders.title",
    subtitle: "showcase.features.reminders.subtitle",
    wiggly: true,
    notifications: [
      { time: "2:36 PM", text: "showcase.notifications.reminder_water" },
      { time: "5:36 PM", text: "showcase.notifications.reminder_gym" },
      { time: "8:36 PM", text: "showcase.notifications.reminder_hustle" },
    ],
  },
];

// Store links
const APP_STORE_URL = "https://apps.apple.com/us/app/gitfit-ai/id6754495386";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=ai.gitfit.app&pcampaignid=web_share";

// Progress color helper for scrolling metrics
const getProgressColor = (percentage: number) => {
  if (percentage >= 90) return "hsl(142 60% 35%)";
  if (percentage >= 70) return "hsl(142 50% 45%)";
  if (percentage >= 50) return "hsl(60 90% 50%)";
  if (percentage >= 30) return "hsl(30 90% 55%)";
  return "hsl(0 80% 55%)";
};

// Sample metrics for scrolling animation
const SHOWCASE_METRICS = [
  { emoji: "🔥", type: "numeric" },
  { emoji: "💧", type: "numeric" },
  { emoji: "🏃", type: "numeric" },
  { emoji: "😴", type: "numeric" },
  { emoji: "🍎", type: "numeric" },
  { emoji: "💪", type: "numeric" },
  { emoji: "🧘", type: "boolean" },
  { emoji: "📚", type: "numeric" },
  { emoji: "☕", type: "numeric" },
  { emoji: "🎯", type: "boolean" },
  { emoji: "🚶", type: "numeric" },
  { emoji: "🥗", type: "numeric" },
  { emoji: "💊", type: "boolean" },
  { emoji: "🧠", type: "numeric" },
  { emoji: "🎹", type: "numeric" },
  { emoji: "⚽", type: "numeric" },
];

// Header/Footer component with logo and store buttons
interface StoreLinksProps {
  className?: string;
  source?: string;
}

const StoreLinks = ({ className = "", source = "showcase" }: StoreLinksProps) => {
  const { t } = useTranslation();
  return (
    <div className={`flex flex-col items-center gap-3 md:gap-4 px-4 overflow-visible ${className}`}>
      {/* Free text */}
      <p className="text-muted-foreground/70 text-sm md:text-base font-light tracking-wide">
        {t('showcase.free_tagline')}
      </p>
      
      {/* Store buttons */}
      <div className="relative z-20 flex flex-row items-center justify-center gap-4 md:gap-5 flex-shrink-0">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackStoreClick("apple", source)}
          className="flex-shrink-0 cursor-pointer rounded-xl transition-all duration-200 ease-out hover:scale-105 hover:brightness-110 hover:drop-shadow-[0_4px_20px_rgba(255,255,255,0.15)] active:scale-95 active:brightness-90"
        >
          <img
            src="/Download_AppStore.png"
            alt={t('showcase.download_app_store')}
            className="h-11 sm:h-14 md:h-16 w-auto object-contain"
          />
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackStoreClick("google", source)}
          className="flex-shrink-0 cursor-pointer rounded-xl transition-all duration-200 ease-out hover:scale-105 hover:brightness-110 hover:drop-shadow-[0_4px_20px_rgba(255,255,255,0.15)] active:scale-95 active:brightness-90"
        >
          <img
            src="/Download_PlayStore.png"
            alt={t('showcase.download_play_store')}
            className="h-11 sm:h-14 md:h-16 w-auto object-contain"
          />
        </a>
      </div>
      
      {/* Try in browser option */}
      <div className="relative z-10 overflow-visible w-full max-w-[260px] sm:max-w-[300px] md:max-w-[340px]">
        <div className="rainbow-btn-glow" />
        <Button
          variant="outline"
          size="sm"
          className="relative z-10 w-full h-9 rounded-full font-light text-xs text-muted-foreground/70 hover:text-foreground/90 border border-white/[0.08] hover:border-white/20 bg-white/[0.03] backdrop-blur-2xl hover:bg-white/[0.06] transition-all duration-500"
          asChild
        >
          <a href="https://www.gitfit.ai/onboarding">
            {t('showcase.try_in_browser')}
            <Play className="ml-1.5 w-3 h-3 opacity-50" />
          </a>
        </Button>
      </div>
    </div>
  );
};


// Feature section component
interface FeatureSectionProps {
  feature: Feature;
  index: number;
  isVisible: boolean;
}

const FeatureSection = ({ feature, index, isVisible }: FeatureSectionProps) => {
  const { t } = useTranslation();
  const isLeft = index % 2 === 0;
  const isMobile = useIsMobile();
  const isFirst = index === 0;

  return (
    <motion.section
      id={feature.id}
      className={`min-h-[50vh] md:min-h-[85vh] flex items-center justify-center ${isFirst ? 'pt-4 pb-16 md:pt-8' : 'py-8 pb-16 md:py-16'} px-4 md:px-8`}
      initial={{ opacity: 0.6 }}
      animate={{ opacity: isVisible ? 1 : 0.6 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-6xl w-full">
        <div className={`flex ${isLeft ? 'flex-row' : 'flex-row-reverse'} items-center gap-2 md:gap-10 lg:gap-14`}>
          {/* iPhone with video */}
          <motion.div
            initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="flex-shrink-0"
          >
            <IPhoneFrame
              videoSrc={feature.videoSrc}
              placeholder={!feature.videoSrc && !feature.notifications && !feature.customContent}
              placeholderText={t('showcase.video_coming_soon')}
              notifications={feature.notifications}
              wiggly={feature.wiggly}
              customContent={feature.customContent === 'accuracy' ? <ResearchAccuracyDisplay /> : undefined}
              thumbnails={feature.videoSrc ? VIDEO_THUMBNAILS[feature.videoSrc] : undefined}
            />
          </motion.div>

          {/* Text content */}
          <motion.div
            className={`flex-1 ${isLeft ? 'text-left' : 'text-right'} px-2 md:px-2 overflow-visible`}
            initial={{ opacity: 0, x: isLeft ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
          >
            <div className="text-3xl sm:text-4xl md:text-5xl mb-2 md:mb-3 leading-normal">
              {feature.emoji}
            </div>
            <h2 className="text-base sm:text-2xl md:text-4xl lg:text-5xl font-semibold mb-2 md:mb-4 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent leading-normal pb-2">
              {t(feature.title as any)}
            </h2>
            <p className="text-sm sm:text-base md:text-xl text-muted-foreground/80 mb-3 md:mb-6 leading-relaxed">
              {t(feature.subtitle as any)}
            </p>
            {feature.description && (
              <ul className={`space-y-1 md:space-y-3 ${isLeft ? 'text-left' : 'text-right'}`}>
                {feature.description.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-1 md:gap-3 text-[10px] sm:text-xs md:text-base text-muted-foreground"
                    style={{ justifyContent: isLeft ? 'flex-start' : 'flex-end' }}
                  >
                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-emerald-400/60 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Progress indicator component
interface ProgressIndicatorProps {
  totalSections: number;
  currentSection: number;
  onDotClick: (index: number) => void;
}

const ProgressIndicator = ({ totalSections, currentSection, onDotClick }: ProgressIndicatorProps) => {
  const isMobile = useIsMobile();
  const dotSpacing = isMobile ? 24 : 32; // spacing between dots
  const totalHeight = (totalSections - 1) * dotSpacing;
  
  return (
    <div className="fixed left-1 sm:left-2 md:left-4 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center">
      {/* Background line */}
      <div 
        className="absolute left-1/2 -translate-x-1/2 w-[1.5px] bg-white/10 rounded-full"
        style={{ height: totalHeight, top: 8 }}
      />
      
      {/* Animated progress line */}
      <motion.div 
        className="absolute left-1/2 -translate-x-1/2 w-[1.5px] bg-emerald-400/60 rounded-full origin-top"
        style={{ top: 8 }}
        initial={{ height: 0 }}
        animate={{ height: currentSection * dotSpacing }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      />
      
      {/* Dots */}
      <div className="relative flex flex-col" style={{ gap: dotSpacing - 8 }}>
        {Array.from({ length: totalSections }).map((_, index) => {
          const isActive = index === currentSection;
          const isPast = index < currentSection;
          
          return (
            <button
              key={index}
              onClick={() => onDotClick(index)}
              className="relative w-2 h-2 flex items-center justify-center focus:outline-none group"
              aria-label={`Go to section ${index + 1}`}
            >
              {/* Outer glow for active */}
              {isActive && (
                <motion.div
                  className="absolute inset-0 bg-emerald-400 rounded-full"
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{ scale: 2.5, opacity: 0 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "easeOut" }}
                />
              )}
              
              {/* Dot */}
              <motion.div
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  isActive 
                    ? 'bg-emerald-400' 
                    : isPast 
                      ? 'bg-emerald-400/50' 
                      : 'bg-white/20'
                }`}
                animate={{ 
                  scale: isActive ? 1.5 : 1,
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
              
              {/* Hover effect */}
              <div className="absolute inset-0 -m-2 rounded-full group-hover:bg-primary/10 transition-colors" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Square morphing animation modes for hero section
const SQUARE_MODE_COUNT = 5;
const SQUARE_CYCLE_MS = 1400;

// Color palette
const SQ_M = 'hsl(var(--muted))';
const SQ_G = '#39d353';
const SQ_R = '#ef4444';

type SquareTarget = { bg: string; scale: number; y: number };

const getSquareTargets = (mode: number): SquareTarget[] => {
  switch (mode) {
    case 0:
      return [
        { bg: SQ_M, scale: 1, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 25%, hsl(var(--muted)))', scale: 1, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 50%, hsl(var(--muted)))', scale: 1, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 75%, hsl(var(--muted)))', scale: 1, y: 0 },
        { bg: SQ_G, scale: 1, y: 0 },
        { bg: SQ_G, scale: 1, y: 0 },
        { bg: 'color-mix(in srgb, #ef4444 50%, hsl(var(--muted)))', scale: 1, y: 0 },
        { bg: SQ_R, scale: 1, y: 0 },
      ];
    case 1:
      return [
        { bg: SQ_M, scale: 0.3, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 15%, hsl(var(--muted)))', scale: 0.4, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 30%, hsl(var(--muted)))', scale: 0.5, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 45%, hsl(var(--muted)))', scale: 0.6, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 60%, hsl(var(--muted)))', scale: 0.7, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 75%, hsl(var(--muted)))', scale: 0.8, y: 0 },
        { bg: 'color-mix(in srgb, #39d353 90%, hsl(var(--muted)))', scale: 0.9, y: 0 },
        { bg: SQ_G, scale: 1, y: 0 },
      ];
    case 2:
      return [
        { bg: SQ_R, scale: 1, y: 0 },
        { bg: '#e06830', scale: 1, y: 0 },
        { bg: '#c88c20', scale: 1, y: 0 },
        { bg: '#a5a825', scale: 1, y: 0 },
        { bg: '#82b830', scale: 1, y: 0 },
        { bg: '#65c63a', scale: 1, y: 0 },
        { bg: '#4ccc44', scale: 1, y: 0 },
        { bg: SQ_G, scale: 1, y: 0 },
      ];
    case 3:
      return Array.from({ length: 8 }, (_, i) => {
        if (i === 3) return { bg: SQ_M, scale: 1, y: 0 };
        if (i === 4) return { bg: SQ_G, scale: 1, y: 0 };
        return { bg: SQ_M, scale: 0, y: 0 };
      });
    case 4:
      return Array.from({ length: 8 }, (_, i) => {
        if (i === 3) return { bg: SQ_M, scale: 1, y: 0 };
        if (i === 4) return { bg: SQ_R, scale: 1, y: 0 };
        return { bg: SQ_M, scale: 0, y: 0 };
      });
    default:
      return getSquareTargets(0);
  }
};

// Abstract grid configuration for hero background
const GRID_COLS = 26;
const GRID_ROWS = 7;
const GRID_TOTAL = GRID_COLS * GRID_ROWS;

// Generate deterministic "contribution" intensity for each cell using a seed hash
const getGridCellIntensity = (col: number, row: number, wave: number): number => {
  const seed = Math.sin(col * 3.7 + row * 2.3 + wave * 0.4) * 0.5 + 0.5;
  const wave2 = Math.sin(col * 0.3 + wave * 0.15) * 0.5 + 0.5;
  const wave3 = Math.cos(row * 0.8 + wave * 0.2) * 0.5 + 0.5;
  const combined = seed * 0.4 + wave2 * 0.35 + wave3 * 0.25;
  if (combined > 0.78) return 4;
  if (combined > 0.58) return 3;
  if (combined > 0.38) return 2;
  if (combined > 0.2) return 1;
  return 0;
};

const GRID_COLORS: Record<number, string> = {
  0: 'rgba(255,255,255,0.03)',
  1: 'rgba(57,211,83,0.15)',
  2: 'rgba(57,211,83,0.30)',
  3: 'rgba(57,211,83,0.50)',
  4: 'rgba(57,211,83,0.75)',
};

// Hero Abstract Grid — a large contribution-graph style grid behind the hero
const HeroAbstractGrid = ({ wave }: { wave: number }) => {
  return (
    <div className="grid gap-[3px] sm:gap-1 md:gap-[5px]" style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}>
      {Array.from({ length: GRID_TOTAL }).map((_, idx) => {
        const col = idx % GRID_COLS;
        const row = Math.floor(idx / GRID_COLS);
        const intensity = getGridCellIntensity(col, row, wave);
        return (
          <motion.div
            key={idx}
            className="aspect-square rounded-[3px] sm:rounded-[4px]"
            animate={{
              backgroundColor: GRID_COLORS[intensity],
              scale: intensity >= 3 ? [1, 1.15, 1] : 1,
            }}
            transition={{
              backgroundColor: { duration: 1.2, ease: 'easeInOut' },
              scale: { duration: 2.4, repeat: Infinity, repeatType: 'mirror', delay: (col + row) * 0.04 },
            }}
            style={{
              boxShadow: intensity >= 3 ? `0 0 ${intensity * 4}px rgba(57,211,83,${intensity * 0.08})` : 'none',
            }}
          />
        );
      })}
    </div>
  );
};

// Individual morphing square with staggered jelly animation
const MorphingSquare = ({
  index,
  target,
  entered,
}: {
  index: number;
  target: SquareTarget;
  entered: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const prevScale = useRef(0);
  const [animState, setAnimState] = useState({
    scaleX: 0,
    scaleY: 0,
    y: 0,
    opacity: 0,
  });

  const staggerDelay = index * 55;

  useEffect(() => {
    if (!entered) {
      const timer = setTimeout(() => {
        setAnimState({ scaleX: 1, scaleY: 1, y: 0, opacity: 1 });
      }, 800 + index * 60);
      return () => clearTimeout(timer);
    }

    // On mode change: jelly squash/stretch wave
    const isGrowing = target.scale > prevScale.current;
    const isShrinking = target.scale < prevScale.current && target.scale < 0.1;
    prevScale.current = target.scale;

    if (isShrinking) {
      // Shrink: squash down then disappear
      const t1 = setTimeout(() => {
        setAnimState({
          scaleX: 1.3,
          scaleY: 0.5,
          y: 6,
          opacity: 1,
        });
      }, staggerDelay);
      const t2 = setTimeout(() => {
        setAnimState({
          scaleX: 0,
          scaleY: 0,
          y: 0,
          opacity: 0,
        });
      }, staggerDelay + 120);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }

    if (target.scale < 0.1 && prevScale.current < 0.1) {
      // Stay hidden
      setAnimState({ scaleX: 0, scaleY: 0, y: 0, opacity: 0 });
      return;
    }

    // Normal transition: overshoot vertically, then settle
    const t1 = setTimeout(() => {
      setAnimState({
        scaleX: target.scale * 0.85,
        scaleY: target.scale * 1.2,
        y: -4,
        opacity: 1,
      });
    }, staggerDelay);

    const t2 = setTimeout(() => {
      setAnimState({
        scaleX: target.scale * 1.08,
        scaleY: target.scale * 0.92,
        y: 2,
        opacity: 1,
      });
    }, staggerDelay + 150);

    const t3 = setTimeout(() => {
      setAnimState({
        scaleX: target.scale,
        scaleY: target.scale,
        y: target.y,
        opacity: 1,
      });
    }, staggerDelay + 280);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [target.scale, target.y, entered]);

  // Update background color via ref for smoother CSS transitions
  useEffect(() => {
    if (ref.current) {
      // Stagger the color change too
      const timer = setTimeout(() => {
        if (ref.current) ref.current.style.backgroundColor = target.bg;
      }, entered ? staggerDelay : 800 + index * 60);
      return () => clearTimeout(timer);
    }
  }, [target.bg, entered]);

  return (
    <div
      ref={ref}
      className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-md sm:rounded-lg shadow-lg"
      style={{
        backgroundColor: SQ_M,
        transform: `scaleX(${animState.scaleX}) scaleY(${animState.scaleY}) translateY(${animState.y}px)`,
        opacity: animState.opacity,
        transition: entered
          ? `transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease-out, background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1)`
          : `transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease-out, background-color 0.4s ease-out`,
        transformOrigin: 'center bottom',
        willChange: 'transform, opacity, background-color',
      }}
    />
  );
};

const Showcase = () => {
  const { t } = useTranslation();
  const [currentSection, setCurrentSection] = useState(0);
  const [showNextButton, setShowNextButton] = useState(true);
  const [isPastHeader, setIsPastHeader] = useState(false);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [squareMode, setSquareMode] = useState(0);
  const [squaresEntered, setSquaresEntered] = useState(false);
  const [gridWave, setGridWave] = useState(0);

  // Start cycling square modes after entrance animation completes
  useEffect(() => {
    const entranceTimer = setTimeout(() => setSquaresEntered(true), 1400);
    return () => clearTimeout(entranceTimer);
  }, []);

  useEffect(() => {
    if (!squaresEntered) return;
    const interval = setInterval(() => {
      setSquareMode((prev) => (prev + 1) % SQUARE_MODE_COUNT);
    }, SQUARE_CYCLE_MS);
    return () => clearInterval(interval);
  }, [squaresEntered]);

  // Cycle the hero grid wave for living animation
  useEffect(() => {
    const interval = setInterval(() => {
      setGridWave(prev => prev + 1);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  // Auto-detect user's language on first visit
  useEffect(() => {
    detectAndApplyLanguage();
  }, []);

  // Track which section is currently in view based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportCenter = window.innerHeight / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      
      sectionRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const sectionCenter = rect.top + rect.height / 2;
          const distance = Math.abs(sectionCenter - viewportCenter);
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        }
      });
      
      setCurrentSection(closestIndex);
      // Always show the button (it becomes Try online on footer)
      setShowNextButton(true);
      
      // Show progress bar once user has scrolled at least 50px
      setHasScrolled(scrollY > 50);
      
      // Show compact header once user scrolls past the hero area (first feature visible)
      const firstSection = sectionRefs.current[0];
      const pastHeroArea = firstSection
        ? firstSection.getBoundingClientRect().top < window.innerHeight * 0.4
        : false;
      setIsPastHeader(pastHeroArea && closestIndex < features.length - 1);
    };

    // Run on mount
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  // Smooth scroll to next section
  const scrollToNext = () => {
    // Show progress bar immediately when button is clicked
    setHasScrolled(true);
    
    // Check if we're above the first section (in header area)
    const firstSection = sectionRefs.current[0];
    if (firstSection) {
      const rect = firstSection.getBoundingClientRect();
      // If first section is mostly below viewport, scroll to it
      if (rect.top > window.innerHeight * 0.4) {
        document.getElementById(features[0].id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    
    const nextIndex = Math.min(currentSection + 1, features.length);
    if (nextIndex >= features.length) {
      document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      document.getElementById(features[nextIndex].id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Scroll to specific section by index
  const scrollToSection = (index: number) => {
    const target = document.getElementById(features[index].id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Glassmorphism + Rainbow glow styles */}
      <style>{`
        @keyframes rainbow-btn-move {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes rainbow-btn-fade {
          0% { opacity: 0.18; }
          70% { opacity: 0.18; }
          100% { opacity: 0; }
        }
        .rainbow-btn-glow {
          position: absolute;
          inset: -4px;
          border-radius: 9999px;
          background: linear-gradient(
            45deg,
            #ff000088, #ff7f0088, #ffff0088, #00ff0088, #0000ff88, #4b008288, #9400d388,
            #ff000088, #ff7f0088, #ffff0088, #00ff0088, #0000ff88, #4b008288, #9400d388
          );
          background-size: 400% 400%;
          filter: blur(14px);
          z-index: 0;
          opacity: 0;
          animation: rainbow-btn-move 10s ease infinite, rainbow-btn-fade 5s ease forwards;
          pointer-events: none;
        }

      `}</style>
      {/* Fixed top-left logo — glassmorphism pill when scrolled */}
      <motion.div 
        className="fixed top-4 left-4 sm:top-6 sm:left-6 md:top-8 md:left-8 z-50"
        animate={{
          scale: isPastHeader ? 0.65 : 1,
        }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{ transformOrigin: 'top left' }}
      >
        <div className="flex items-center gap-2.5 sm:gap-3">
          <img
            src="/brand-logo-nobg.png"
            alt="GitFit.ai"
            className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto object-contain"
          />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground lowercase tracking-tight">
            gitfit<span className="font-extralight opacity-40">.ai</span>
          </h2>
        </div>
      </motion.div>

      {/* Language selector — fixed top right */}
      <div className="fixed top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 z-50 flex items-center gap-2 sm:gap-3">
        {/* Mini store buttons - fade in when scrolled */}
        <AnimatePresence>
          {isPastHeader && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2 sm:gap-3"
            >
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackStoreClick("apple", "showcase_navbar")}
                className="cursor-pointer rounded-lg transition-all duration-200 ease-out hover:scale-110 hover:brightness-110 hover:drop-shadow-[0_2px_12px_rgba(255,255,255,0.15)] active:scale-95 active:brightness-90"
              >
                <img
                  src="/Download_AppStore.png"
                  alt={t('showcase.download_app_store')}
                  className="h-8 sm:h-9 md:h-10 w-auto"
                />
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackStoreClick("google", "showcase_navbar")}
                className="cursor-pointer rounded-lg transition-all duration-200 ease-out hover:scale-110 hover:brightness-110 hover:drop-shadow-[0_2px_12px_rgba(255,255,255,0.15)] active:scale-95 active:brightness-90"
              >
                <img
                  src="/Download_PlayStore.png"
                  alt={t('showcase.download_play_store')}
                  className="h-8 sm:h-9 md:h-10 w-auto"
                />
              </a>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {!isPastHeader && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <LanguageSelector />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Progress indicator - show when user scrolls or clicks "See more" */}
      <AnimatePresence mode="wait">
        {hasScrolled && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <ProgressIndicator
              totalSections={features.length}
              currentSection={currentSection}
              onDotClick={scrollToSection}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Flowing Metrics Snake - circles travel ALONG the curved path */}
      <div className="absolute pointer-events-none overflow-hidden z-0" style={{ top: '60vh', left: 0, right: 0, height: '680vh' }}>
        {(() => {
          // Generate keyframe positions along the snake path
          // The snake curves to the TEXT side at the CENTER of each slide
          const generatePathKeyframes = () => {
            const keyframes: { x: number; y: number }[] = [];
            const numKeyframes = 100;
            
            // 8 feature slides, each taking 1/8 of the container
            const numSlides = 8;
            
            for (let i = 0; i <= numKeyframes; i++) {
              const progress = i / numKeyframes;
              const yPercent = progress * 100;
              
              // Which slide are we in? (0-7)
              const slideIndex = Math.min(Math.floor(progress * numSlides), numSlides - 1);
              
              // Progress within this slide (0 to 1)
              const slideProgress = (progress * numSlides) % 1;
              
              // Use sine wave: peaks at 0.5 (center of slide)
              // sin(0) = 0, sin(π/2) = 1, sin(π) = 0
              const waveValue = Math.sin(slideProgress * Math.PI);
              
              // Even slides (0,2,4,6): phone LEFT, text RIGHT → snake on RIGHT
              // Odd slides (1,3,5,7): phone RIGHT, text LEFT → snake on LEFT
              const isEvenSlide = slideIndex % 2 === 0;
              
              let x: number;
              if (isEvenSlide) {
                // Snake on right side, peak at 88%
                x = 50 + (waveValue * 38); // 50% → 88% → 50%
              } else {
                // Snake on left side, peak at 12%
                x = 50 - (waveValue * 38); // 50% → 12% → 50%
              }
              
              keyframes.push({ x, y: yPercent });
            }
            
            return keyframes;
          };
          
          const pathKeyframes = generatePathKeyframes();
          const xKeyframes = pathKeyframes.map(k => `${k.x}%`);
          const yKeyframes = pathKeyframes.map(k => `${k.y}%`);
          
          // Create multiple circles that travel along the path with staggered starts
          // 4x more circles (80 instead of 20), traveling 2.5x slower (75s instead of 30s)
          const numCircles = 80;
          const animationDuration = 75;
          
          return Array.from({ length: numCircles }).map((_, idx) => {
            const metric = SHOWCASE_METRICS[idx % SHOWCASE_METRICS.length];
            const seed = idx;
            const isBoolean = metric.type === "boolean";
            const randomValue = isBoolean
              ? (seed % 2 === 0 ? 100 : 0)
              : 30 + (seed * 11) % 70;
            
            const radius = 12;
            const circumference = 2 * Math.PI * radius;
            const strokeDashoffset = circumference - (randomValue / 100) * circumference;
            
            // Stagger the animation start so circles are spread along the path
            const delay = (idx / numCircles) * -animationDuration; // Negative delay = starts partway through
            
            // Opacity keyframes: fade in over first 15% of path, stay visible, fade out at end
            const opacityKeyframes = pathKeyframes.map((k, i) => {
              const progress = i / pathKeyframes.length;
              if (progress < 0.15) {
                // Fade in during first 15%
                return (progress / 0.15) * 0.35;
              } else if (progress > 0.9) {
                // Fade out during last 10%
                return ((1 - progress) / 0.1) * 0.35;
              }
              return 0.35;
            });

            return (
              <motion.div
                key={idx}
                className="absolute"
                style={{
                  transform: 'translate(-50%, -50%)',
                }}
                animate={{
                  left: xKeyframes,
                  top: yKeyframes,
                  opacity: opacityKeyframes,
                }}
                transition={{
                  duration: animationDuration,
                  repeat: Infinity,
                  repeatType: 'loop',
                  ease: 'linear',
                  delay: delay,
                }}
              >
                <svg className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 32 32">
                  <circle
                    cx="16"
                    cy="16"
                    r={radius}
                    stroke="hsl(var(--muted))"
                    strokeWidth="1.5"
                    fill="none"
                    transform="rotate(-90 16 16)"
                  />
                  <circle
                    cx="16"
                    cy="16"
                    r={radius}
                    stroke={getProgressColor(randomValue)}
                    strokeWidth="1.5"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    transform="rotate(-90 16 16)"
                  />
                  <text x="16" y="20" textAnchor="middle" fontSize="12" fill="currentColor" opacity="0.7">
                    {metric.emoji}
                  </text>
                </svg>
              </motion.div>
            );
          });
        })()}
      </div>
      
      {/* Gradient mesh background — refined for glassmorphism */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-emerald-500/[0.07] rounded-full blur-[120px]" />
        <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] bg-cyan-500/[0.05] rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/3 w-[350px] h-[350px] bg-violet-500/[0.04] rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-emerald-400/[0.03] rounded-full blur-[80px]" />
        {/* subtle noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%270 0 256 256%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%274%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/%3E%3C/svg%3E")' }} />
      </div>
      
      {/* ═══════════ HERO ═══════════ */}
      <header className="relative pt-28 sm:pt-32 md:pt-36 pb-8 md:pb-12 min-h-[92vh] md:min-h-[94vh] flex flex-col items-center justify-center overflow-hidden">
        {/* ── Abstract Grid Background ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-0"
          style={{
            maskImage: 'radial-gradient(ellipse 80% 70% at 50% 45%, black 20%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 45%, black 20%, transparent 75%)',
          }}
        >
          <div className="w-[90vw] max-w-[900px] opacity-70">
            <HeroAbstractGrid wave={gridWave} />
          </div>
        </motion.div>

        {/* ── Center vignette: darkens + blurs grid behind text ── */}
        <div
          className="absolute inset-0 pointer-events-none z-[1]"
          style={{
            background: 'radial-gradient(ellipse 55% 45% at 50% 46%, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0) 70%)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            maskImage: 'radial-gradient(ellipse 55% 45% at 50% 46%, black 0%, black 30%, transparent 65%)',
            WebkitMaskImage: 'radial-gradient(ellipse 55% 45% at 50% 46%, black 0%, black 30%, transparent 65%)',
          }}
        />

        {/* ── Hero content ── */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 text-center max-w-3xl mx-auto px-6"
        >
            <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 md:mb-6 tracking-tight leading-[1.15] break-words">
              {t('showcase.hero_title')}{' '}
              <span className="bg-gradient-to-r from-[#39d353] via-emerald-400 to-[#39d353] bg-clip-text text-transparent">
                {t('showcase.hero_squares')}
              </span>
            </h1>

            {/* Morphing squares row inside the card */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 md:gap-4 mb-8 md:mb-10">
              {getSquareTargets(squareMode).map((target, i) => (
                <MorphingSquare key={i} index={i} target={target} entered={squaresEntered} />
              ))}
            </div>

            <StoreLinks source="showcase_hero" />
        </motion.div>

        {/* ── Scrolling Metrics — sits below the glass card ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="relative z-10 w-full opacity-40 mt-10 md:mt-14"
        >
          <div
            className="relative w-full overflow-hidden h-10 md:h-12"
            style={{
              maskImage: 'linear-gradient(to right, transparent 2%, black 18%, black 82%, transparent 98%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 2%, black 18%, black 82%, transparent 98%)',
            }}
          >
            <motion.div
              className="flex gap-6 md:gap-8 absolute"
              animate={{ x: [0, -800] }}
              transition={{ x: { repeat: Infinity, repeatType: 'loop', duration: 30, ease: 'linear' } }}
            >
              {[...SHOWCASE_METRICS, ...SHOWCASE_METRICS, ...SHOWCASE_METRICS].map((metric, idx) => {
                const isBoolean = metric.type === 'boolean';
                const seed = idx % SHOWCASE_METRICS.length;
                const randomValue = isBoolean ? (seed % 2 === 0 ? 100 : 0) : 40 + (seed * 7) % 60;
                const radius = 14;
                const circumference = 2 * Math.PI * radius;
                const strokeDashoffset = circumference - (randomValue / 100) * circumference;
                return (
                  <div key={idx} className="flex-shrink-0">
                    <svg className="w-10 h-10 md:w-12 md:h-12" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r={radius} stroke="hsl(var(--muted))" strokeWidth="2" fill="none" transform="rotate(-90 18 18)" />
                      <circle cx="18" cy="18" r={radius} stroke={getProgressColor(randomValue)} strokeWidth="2" fill="none" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" transform="rotate(-90 18 18)" />
                      <text x="18" y="23" textAnchor="middle" fontSize="16" fill="currentColor">{metric.emoji}</text>
                    </svg>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </motion.div>
      </header>
      
      {/* Feature sections */}
      {features.map((feature, index) => (
        <div
          key={feature.id}
          ref={(el) => (sectionRefs.current[index] = el)}
        >
          <FeatureSection
            feature={feature}
            index={index}
            isVisible={currentSection === index}
          />
        </div>
      ))}
      
      {/* ═══════════ FOOTER ═══════════ */}
      <footer
        id="footer"
        ref={(el) => (sectionRefs.current[features.length] = el)}
        className="relative py-16 md:py-24 lg:py-32 min-h-[60vh] flex flex-col items-center justify-center px-4"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center w-full max-w-lg"
        >
          <div className="w-full flex flex-col items-center">
            {/* Mini abstract grid */}
            <div className="opacity-40 mb-6">
              <div className="flex gap-1">
                {Array.from({ length: 16 }, (_, weekIndex) => (
                  <div key={weekIndex} className="flex flex-col gap-1">
                    {Array.from({ length: 5 }, (_, dayIndex) => {
                      const random = Math.sin(weekIndex * 3.7 + dayIndex * 2.3) * 0.5 + 0.5;
                      const intensity = random > 0.7 ? 4 : random > 0.5 ? 3 : random > 0.3 ? 2 : random > 0.15 ? 1 : 0;
                      const colors = {
                        0: 'rgba(255,255,255,0.04)',
                        1: 'rgba(57,211,83,0.15)',
                        2: 'rgba(57,211,83,0.30)',
                        3: 'rgba(57,211,83,0.50)',
                        4: 'rgba(57,211,83,0.70)',
                      };
                      return (
                        <div
                          key={dayIndex}
                          className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-[2px]"
                          style={{ backgroundColor: colors[intensity as keyof typeof colors] }}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            <StoreLinks source="showcase_footer" />
          </div>
        </motion.div>

        {/* Footer links */}
        <div className="mt-8 md:mt-12 text-center text-xs md:text-sm text-muted-foreground/60 px-4">
          <a href="/privacy-policy" className="hover:text-foreground/80 transition-colors">
            {t('showcase.footer.privacy_policy')}
          </a>
          <span className="mx-2 opacity-40">·</span>
          <a href="/terms-and-conditions" className="hover:text-foreground/80 transition-colors">
            {t('showcase.footer.terms_of_service')}
          </a>
          <span className="mx-2 opacity-40">·</span>
          <a href="/about" className="hover:text-foreground/80 transition-colors">
            {t('showcase.footer.about')}
          </a>
        </div>

        <p className="mt-3 md:mt-4 text-center text-xs md:text-sm text-muted-foreground/40">
          © {new Date().getFullYear()} GitFit.AI. {t('showcase.footer.all_rights_reserved')}
        </p>
      </footer>
      
      {/* Floating next button */}
      <AnimatePresence>
        {showNextButton && (
          <motion.div
            className="fixed bottom-6 md:bottom-8 left-0 right-0 z-50 flex justify-center pointer-events-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col items-center gap-5">
              {/* Try online button - appears above Next when scrolled past header */}
              <AnimatePresence>
                {isPastHeader && currentSection < features.length && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="relative">
                      {!isMobile && <div className="rainbow-btn-glow" />}
                      <Button
                        asChild
                        size="lg"
                        variant="outline"
                        className="relative z-10 pointer-events-auto rounded-full px-6 h-12 backdrop-blur-2xl shadow-xl transition-all hover:scale-105 active:scale-95 border-white/10 hover:border-white/20 bg-white/[0.04] hover:bg-white/[0.08]"
                      >
                        <a href="https://www.gitfit.ai/onboarding">
                          <span className="mr-2">{t('showcase.try_online')}</span>
                          <Play className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <motion.div
                animate={
                  !isPastHeader
                    ? {
                        y: [0, -8, 0],
                        scale: [1, 1.05, 1],
                      }
                    : {}
                }
                transition={
                  !isPastHeader
                    ? {
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }
                    : {}
                }
              >
                {currentSection >= features.length ? (
                  <div className="relative">
                    {!isMobile && <div className="rainbow-btn-glow" />}
                    <Button
                      asChild
                      size="lg"
                      className="relative z-10 pointer-events-auto rounded-full px-8 h-12 bg-emerald-500/80 backdrop-blur-xl hover:bg-emerald-500/90 shadow-xl shadow-emerald-500/20 border border-emerald-400/20 transition-all hover:scale-105 active:scale-95"
                    >
                      <a href="https://www.gitfit.ai/onboarding">
                        <span className="mr-2">{t('showcase.try_online')}</span>
                        <Play className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={scrollToNext}
                    size="lg"
                    className={`pointer-events-auto rounded-full backdrop-blur-xl border transition-all duration-500 hover:scale-105 active:scale-95 ${
                      !isPastHeader
                        ? 'px-12 h-16 text-lg bg-emerald-500/80 hover:bg-emerald-500/90 shadow-xl shadow-emerald-500/20 border-emerald-400/20'
                        : 'px-8 h-12 text-sm bg-white/[0.12] hover:bg-white/[0.18] border-white/20 hover:border-white/30 shadow-lg text-white'
                    }`}
                  >
                    <span className="mr-2">{!isPastHeader ? t('showcase.see_more') : t('showcase.next')}</span>
                    <ChevronDown className={`transition-all duration-500 ${!isPastHeader ? 'w-6 h-6' : 'w-5 h-5'}`} />
                  </Button>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Bottom padding to prevent button overlap on mobile */}
      <div className="h-24 md:h-0" />
    </div>
  );
};

export default Showcase;
